import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Sparkles, Bell, X } from 'lucide-react';
import { useSocket } from '../context/SocketContext';
import { photoUrl, dicebear } from '../utils/api';

const TYPE_ICON = { message: MessageCircle, wish_received: Sparkles };

export default function NotificationPeek() {
  const { peekNotif, dismissPeek } = useSocket();
  const navigate = useNavigate();
  const [visible,  setVisible]  = useState(false);
  const [leaving,  setLeaving]  = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const leaveTimer = useRef(null);

  useEffect(() => {
    if (peekNotif) {
      clearTimeout(leaveTimer.current);
      setSnapshot(peekNotif);
      setLeaving(false);
      setVisible(true);
    } else if (visible) {
      setLeaving(true);
      leaveTimer.current = setTimeout(() => {
        setVisible(false);
        setLeaving(false);
      }, 240);
    }
    return () => clearTimeout(leaveTimer.current);
  }, [peekNotif]); // eslint-disable-line

  if (!visible || !snapshot) return null;

  const TypeIcon = TYPE_ICON[snapshot.type] || Bell;
  const avatar = snapshot.actorPhoto
    ? (photoUrl(snapshot.actorPhoto) || dicebear(snapshot.actorName))
    : dicebear(snapshot.actorName || '?');

  const handleClick = () => {
    dismissPeek();
    if (snapshot.type === 'message' && snapshot.linkRoomId) {
      navigate(`/chat/${snapshot.linkRoomId}`);
    }
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={`${snapshot.actorName}: ${snapshot.body}. Tap to open.`}
        aria-live="polite"
        onClick={handleClick}
        onKeyDown={e => e.key === 'Enter' && handleClick()}
        style={{
          position: 'fixed',
          top: 'calc(44px + env(safe-area-inset-top, 0px) + 8px)',
          right: '12px',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: 'var(--glass-bg)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: '1px solid var(--glass-border)',
          borderRadius: '16px',
          padding: '12px 16px',
          cursor: 'pointer',
          width: 'min(320px, calc(100vw - 24px))',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 12px 40px rgba(0,0,0,0.5)',
          animation: leaving
            ? 'peekSlideUp 0.24s ease-in forwards'
            : 'peekSlideDown 0.36s cubic-bezier(0.34,1.56,0.64,1) forwards',
          overflow: 'hidden',
          fontFamily: 'var(--font-body)',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(2px)';
          e.currentTarget.style.boxShadow = '0 18px 50px rgba(0,0,0,0.6)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = '';
          e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.45)';
        }}
      >
        {/* Avatar */}
        <div style={{
          width: '38px', height: '38px', borderRadius: '50%',
          overflow: 'hidden', flexShrink: 0,
          border: '1.5px solid var(--accent)',
          boxShadow: '0 0 10px rgba(255,78,138,0.25)',
        }}>
          <img
            src={avatar} alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.target.src = dicebear(snapshot.actorName || '?'); }}
          />
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px' }}>
            <TypeIcon size={12} color="var(--accent)" style={{ flexShrink: 0 }} />
            <span style={{
              fontSize: '0.8rem', fontWeight: 700, color: 'var(--accent)',
              fontFamily: 'var(--font-body)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {snapshot.actorName}
            </span>
          </div>
          <div style={{
            fontSize: '0.77rem', color: 'var(--text2)',
            fontFamily: 'var(--font-body)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            lineHeight: 1.4,
          }}>
            {snapshot.body}
          </div>
        </div>

        {/* Dismiss */}
        <button
          onClick={e => { e.stopPropagation(); dismissPeek(); }}
          aria-label="Dismiss notification"
          style={{
            background: 'var(--bg3)', border: 'none',
            color: 'var(--text3)', cursor: 'pointer',
            flexShrink: 0, width: '28px', height: '28px',
            borderRadius: '50%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s, color 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.color = 'var(--text)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.color = 'var(--text3)'; }}
        >
          <X size={14} />
        </button>

        {/* Progress bar */}
        {!leaving && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, height: '3px',
            background: 'linear-gradient(90deg, var(--accent), var(--accent2, #FF8C69))',
            borderRadius: '0 0 16px 16px',
            animation: 'peekProgress 3.5s linear forwards',
          }} />
        )}
      </div>

      <style>{`
        @keyframes peekSlideDown {
          from { opacity: 0; transform: translateY(-16px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)     scale(1);    }
        }
        @keyframes peekSlideUp {
          from { opacity: 1; transform: translateY(0)     scale(1);    }
          to   { opacity: 0; transform: translateY(-16px) scale(0.95); }
        }
        @keyframes peekProgress {
          from { width: 100%; }
          to   { width: 0%;   }
        }
      `}</style>
    </>
  );
}
